import { store } from '../store'
import Token from '../common/token'
import Swal from 'sweetalert2'
import Logger from 'js-logger'
import { Router } from 'vue-router'
import i18n from '@web/plugins/i18n'
import { showWarningSubscription } from '@web/common/subscription'
import { User } from '@web/store/types/modules/user'
import PAGE_NAME from '@web/consts/PAGE_NAME'
import {
  resolveOnboardingDestination,
  shouldCompleteOnboarding,
  ONBOARDING_STEP_ROUTE_NAMES,
  isInsideAccountSetupRoute
} from '@web/platform.v3/onboardings/resolver'
import { clearOnboardingProgress, readOnboardingProgress, writeOnboardingProgress } from '@web/platform.v3/onboardings/progress'
import type { OnboardingContext } from '@web/platform.v3/onboardings/types'
import { selectHasSelfieCourse } from '@web/platform.v3/onboardings/selectors/selectHasSelfieCourse'
import { selectHasFfNmsSegment } from '@web/platform.v3/onboardings/selectors/selectFfNmsSegment'
import { specialProjectId, superSubscriptionSegment, ffNmsSegment } from '@web/common/ExternalConfig/getters'
import STREAM_STATUS from '@web/consts/StreamStatus'

const EXEMPT_FROM_ONBOARDING_LOCK: ReadonlyArray<string> = ['logout', 'login', 'user-info-chat']

const EXEMPT_FROM_SUBSCRIPTION_CHECK: ReadonlyArray<string> = ['user-info-chat', 'updateProfile', 'resetPassword']

function buildLightweightCtx (user: User): OnboardingContext {
  const isShowNewLk = Boolean(store.getters['user/getShowNewLk'])
  const hasSuperSubscription = (user.segments || []).some((segment: Segment) => superSubscriptionSegment() === segment.id)
  const streams = store.getters['stream/streams'] || []
  const firstUserCourseStream = streams.find((stream: { status: string }) => stream.status === STREAM_STATUS.OPEN)
  const firstCourse = firstUserCourseStream
    ? store.getters['course/getCourseById'](firstUserCourseStream.courseId)
    : undefined
  const hasSelfieCourse = selectHasSelfieCourse(user)
  const hasFfNmsSegment = selectHasFfNmsSegment(user, ffNmsSegment())
  const authOptions = store.getters['authV3/getAuthOptions']

  return {
    user,
    isShowNewLk,
    isTake: specialProjectId() === 'take',
    hasSuperSubscription,
    hasSelfieCourse,
    hasFfNmsSegment,
    clubFFExist: true,
    clubFF: undefined,
    firstCourse,
    firstUserCourseStream,
    firstName: user?.firstName || '',
    smsAvailable: authOptions?.smsAvailable ?? false,
    phoneCountry: store.getters['authV3/phoneCountry'] ?? ''
  }
}

export function requestEmailModal (): Promise<boolean> {
  return Swal.fire({
    title: i18n.global.t('requestEmailModal.title'),
    input: 'text',
    inputAttributes: {
      autocapitalize: 'off',
      placeholder: i18n.global.t('input.email')
    },
    confirmButtonText: i18n.global.t('Save'),
    cancelButtonText: i18n.global.t('Cancel'),
    showCancelButton: true,
    focusConfirm: false,
    allowOutsideClick: () => !Swal.isLoading(),
    preConfirm: async (email: string): Promise<boolean> => {
      if (!email) {
        Swal.showValidationMessage(i18n.global.t('error.pleaseEnterEmail'))
      }

      try {
        await store.dispatch('user/updateEmail', email)
        return true
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        let message = i18n.global.t('error.verifyEmail')
        if (error?.errors?.email && error.errors.email.length > 0) {
          message = error.errors.email.join(',')
        }
        Swal.showValidationMessage(message)
        Logger.error(error)
        return false
      }
    }
  })
    .then(result => result.isConfirmed)
}

export default (router: Router): void => {
  router.beforeEach(async (to, from, next) => {
    const user: User = store.getters['user/user']
    const isUserLogged = store.getters['user/isLogged']
    const isUserFetched = store.getters['user/isUserFetched']

    if (to.name === 'logout') {
      return next()
    }

    // Автологин по ?token из URL — только на маршрутах, где это явно разрешено
    // мета-флагом allowUrlToken (opt-in, чтобы не расширять поверхность атаки на весь сайт).
    // Применяем токен только когда он отличается от текущего, затем убираем token из URL.
    const allowUrlToken = to.matched.some(record => record.meta.allowUrlToken)
    const urlTokenRaw = to.query.token
    const urlToken = Array.isArray(urlTokenRaw) ? urlTokenRaw[0] : urlTokenRaw
    if (allowUrlToken && urlToken) {
      const { token: _token, ...restQuery } = to.query
      if (urlToken !== Token.get()) {
        store.dispatch('logout')
        Token.set(String(urlToken))
      }
      // Чистый URL без token (тот же путь и остальные query/params).
      return next({ path: to.path, query: restQuery, hash: to.hash, replace: true })
    }

    if (isUserLogged) {
      // Onboarding lock: force юзера в онбоардинг, если есть активные требования бэка.
      // Поглощает старые force-guards для requirePhoneValidate и requiredUpdateProfile.
      if (
        shouldCompleteOnboarding(buildLightweightCtx(user)) &&
        to.name !== PAGE_NAME.ONBOARDING &&
        !ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !EXEMPT_FROM_ONBOARDING_LOCK.includes(String(to.name))
      ) {
        const dest = resolveOnboardingDestination(buildLightweightCtx(user))
        if (dest.kind === 'onboarding') {
          if (dest.routeName) {
            writeOnboardingProgress({ slug: dest.slug, step: dest.step })
            return next({ name: dest.routeName })
          }
          return next({ name: PAGE_NAME.ONBOARDING, params: { name: dest.slug, step: String(dest.step) } })
        }
      }

      // Если онбоард не требуется и юзер не внутри активного account-setup,
      // но пытаемся попасть на step-route — выкинуть на home.
      if (
        !shouldCompleteOnboarding(buildLightweightCtx(user)) &&
        ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !isInsideAccountSetupRoute(String(to.name))
      ) {
        clearOnboardingProgress()
        return next({ name: PAGE_NAME.HOME })
      }

      // Resume saved progress: если у юзера есть незавершённый онбоард
      // (даже без активных флагов бэка) — возвращаем его на сохранённый шаг.
      const savedProgress = readOnboardingProgress()
      if (
        savedProgress &&
        to.name !== PAGE_NAME.ONBOARDING &&
        !ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !EXEMPT_FROM_ONBOARDING_LOCK.includes(String(to.name))
      ) {
        const dest = resolveOnboardingDestination(buildLightweightCtx(user))
        if (dest.kind === 'onboarding') {
          if (dest.routeName) {
            return next({ name: dest.routeName })
          }
          return next({ name: PAGE_NAME.ONBOARDING, params: { name: dest.slug, step: String(dest.step) } })
        }
      }

      // Проверка на подписку
      if (
        !EXEMPT_FROM_SUBSCRIPTION_CHECK.includes(String(to.name)) &&
        to.name?.toString().indexOf('setting') === -1
      ) {
        if (showWarningSubscription() && to.name !== 'subscriptions') {
          return next({ name: 'subscriptions', params: { warning: 'true' } })
        }
      }
    }

    const isRequiresAuth = to.matched.some(record => record.meta.requiresAuth)
    if (isRequiresAuth && !isUserLogged) {
      try {
        await store.dispatch('user/fetch')
      } catch (error) {
        Logger.error(error)
      }
    }
    if (isRequiresAuth && !Token.get()) {
      store.commit('setReturnUrl', to.fullPath)
      return next({ name: 'login' })
    }

    // Cold-start: user was just fetched, re-run onboarding lock with real user data
    if (isRequiresAuth && !isUserLogged) {
      const freshUser: User = store.getters['user/user']
      if (
        shouldCompleteOnboarding(buildLightweightCtx(freshUser)) &&
        to.name !== PAGE_NAME.ONBOARDING &&
        !ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !EXEMPT_FROM_ONBOARDING_LOCK.includes(String(to.name))
      ) {
        const dest = resolveOnboardingDestination(buildLightweightCtx(freshUser))
        if (dest.kind === 'onboarding') {
          if (dest.routeName) {
            writeOnboardingProgress({ slug: dest.slug, step: dest.step })
            return next({ name: dest.routeName })
          }
          return next({ name: PAGE_NAME.ONBOARDING, params: { name: dest.slug, step: String(dest.step) } })
        }
      }

      if (
        !shouldCompleteOnboarding(buildLightweightCtx(freshUser)) &&
        ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !isInsideAccountSetupRoute(String(to.name))
      ) {
        clearOnboardingProgress()
        return next({ name: PAGE_NAME.HOME })
      }

      // Resume saved progress (cold-start ветка): см. комментарий выше.
      const coldSavedProgress = readOnboardingProgress()
      if (
        coldSavedProgress &&
        to.name !== PAGE_NAME.ONBOARDING &&
        !ONBOARDING_STEP_ROUTE_NAMES.has(String(to.name)) &&
        !EXEMPT_FROM_ONBOARDING_LOCK.includes(String(to.name))
      ) {
        const dest = resolveOnboardingDestination(buildLightweightCtx(freshUser))
        if (dest.kind === 'onboarding') {
          if (dest.routeName) {
            return next({ name: dest.routeName })
          }
          return next({ name: PAGE_NAME.ONBOARDING, params: { name: dest.slug, step: String(dest.step) } })
        }
      }
    }

    if (to.matched.some(record => record.meta.guest) && Token.get()) {
      return next({ name: store.state.assets.homepage })
    }

    if (to.matched.some(record => record.meta.requiresEmail)) {
      if (!user.id) {
        try {
          await store.dispatch('user/fetch')
        } catch (error) {
          Logger.error(error)
        }
      }
      if (!store.getters['user/email']) {
        if (await requestEmailModal()) {
          next()
        } else {
          return next(from)
        }
      }
    }

    if (!isUserFetched) {
      store.commit('user/setIsUserFetched', true)
    }
    next()
  })

  router.afterEach(to => {
    store.commit('router/push', to)
  })
}
